/**
 * SnakeLayoutNode - Generates position arrays following a serpentine grid path
 *
 * Produces posX/posY arrays where instance ordering follows a snake traversal
 * through a grid. Connect outputs to a Repeater so per-instance values
 * (colors, patterns) map spatially in serpentine order.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Snake path generators
// ─────────────────────────────────────────────────────────────

/** Snake from top-left to bottom-right: L→R on even rows, R→L on odd */
function snakeTopLeftToBottomRight(rows: number, cols: number): [number, number][] {
  const path: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    if (r % 2 === 0) {
      for (let c = 0; c < cols; c++) path.push([r, c]);
    } else {
      for (let c = cols - 1; c >= 0; c--) path.push([r, c]);
    }
  }
  return path;
}

/** Snake from bottom-right to top-left: reverse of TL→BR */
function snakeBottomRightToTopLeft(rows: number, cols: number): [number, number][] {
  return snakeTopLeftToBottomRight(rows, cols).reverse();
}

/** Dual snake from corners meeting at center */
function snakeDualToCenter(rows: number, cols: number): [number, number][] {
  const full = snakeTopLeftToBottomRight(rows, cols);
  const total = full.length;
  const half = Math.floor(total / 2);

  const snakeA = full.slice(0, half);
  const snakeB = full.slice(half + 1).reverse();

  // Interleave: A[0], B[0], A[1], B[1], ... then center
  const result: [number, number][] = [];
  const maxLen = Math.max(snakeA.length, snakeB.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < snakeA.length) result.push(snakeA[i]);
    if (i < snakeB.length) result.push(snakeB[i]);
  }

  // Center cell last
  if (total % 2 === 1) {
    result.push(full[half]);
  }

  return result;
}

type SnakeLayout = 'TL->BR' | 'BR->TL' | 'TL->C, BR->C';

const LAYOUT_OPTIONS: SnakeLayout[] = ['TL->BR', 'BR->TL', 'TL->C, BR->C'];

function generateSnakePath(rows: number, cols: number, layout: SnakeLayout): [number, number][] {
  switch (layout) {
    case 'TL->BR': return snakeTopLeftToBottomRight(rows, cols);
    case 'BR->TL': return snakeBottomRightToTopLeft(rows, cols);
    case 'TL->C, BR->C': return snakeDualToCenter(rows, cols);
  }
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const snakeLayoutEndpoints = {
  ...params({
    cols: { value: 3, min: 1, max: 32, step: 1 },
    rows: { value: 3, min: 1, max: 32, step: 1 },
    spacingX: { value: 1, min: 0.01, max: 200, step: 0.01 },
    spacingY: { value: 1, min: 0.01, max: 200, step: 0.01 },
    layout: { dataType: 'string', value: 'TL->C, BR->C', options: LAYOUT_OPTIONS },
    count: { isOutput: true, readonly: true, label: 'Count' },
  }),
  ...internals({
    posX: { dataType: 'array', isOutput: true, label: 'X' },
    posY: { dataType: 'array', isOutput: true, label: 'Y' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class SnakeLayoutNode extends BaseNode<typeof snakeLayoutEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const spacingX = this.params.spacingX.value;
    const spacingY = this.params.spacingY.value;
    const layout = this.params.layout.value as SnakeLayout;

    const path = generateSnakePath(rows, cols, layout);

    // Center the grid at origin
    const offsetX = ((cols - 1) * spacingX) / 2;
    const offsetY = ((rows - 1) * spacingY) / 2;

    const posX: number[] = [];
    const posY: number[] = [];

    for (const [row, col] of path) {
      posX.push(col * spacingX - offsetX);
      posY.push(row * spacingY - offsetY);
    }

    this.internals.posX.set(posX);
    this.internals.posY.set(posY);
    this.params.count.set(posX.length);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const snakeLayoutNodeDef: NodeDef<SnakeLayoutNode> = {
  type: 'SnakeLayout',
  info: 'Generates position arrays following a serpentine grid path for spatial ordering.',
  domain: Domain.Data,
  shortName: 'snakeLayout',
  category: 'Layout',
  endpoints: snakeLayoutEndpoints,
  ctor: SnakeLayoutNode,
};
